import React, { useState } from "react";
import { Pencil, BellRing, Check, Loader2, ChevronDown } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { formatDate } from "@/lib/date";

function Nudge({ m, viewOnly, onReminded }) {
  const [busy, setBusy] = useState(false);
  const remind = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/bios/member/${m.member_id}/remind`);
      toast.success(`Nudge sent to ${m.first_name || m.name}`);
      onReminded(data.reminded_at);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-dashed border-slate-300 bg-slate-50 px-4 py-3" data-testid={`bio-nudge-${m.member_id}`}>
      <p className="text-sm text-slate-600"><span className="font-semibold text-slate-800">No bio yet.</span> Bios are optional. Send a nudge if you'd like {m.first_name || "this member"} to write one.</p>
      {!viewOnly && (
        <div className="flex items-center gap-2">
          {m.reminded_at && <span className="inline-flex items-center gap-1 text-xs text-slate-500" data-testid={`bio-reminded-${m.member_id}`}><Check className="h-3 w-3" /> Nudged {formatDate(m.reminded_at)}</span>}
          <Button size="sm" variant="outline" disabled={busy} onClick={remind} className="rounded-full bg-white" data-testid={`bio-remind-${m.member_id}`}>
            {busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <BellRing className="mr-1.5 h-3.5 w-3.5" />} {m.reminded_at ? "Nudge again" : "Nudge"}
          </Button>
        </div>
      )}
    </div>
  );
}

// One team member on the director's Bio List (collapsible). Members without a bio can be selected for a bulk nudge.
export default function BioRow({ m, viewOnly, onEdit, onReminded, selected, onSelect, open, onToggle, delay = 0 }) {
  const selectable = !viewOnly && !m.bio;
  const stop = (e) => e.stopPropagation();
  return (
    <Card className={`border-slate-200 p-5 shadow-sm animate-fade-in-up transition-colors ${selected ? "border-slate-400 bg-slate-50/60" : ""}`} style={{ animationDelay: `${delay}ms` }} data-testid={`bio-row-${m.member_id}`}>
      <div className="flex cursor-pointer select-none items-start justify-between gap-3" onClick={() => onToggle(m.member_id)}
        role="button" aria-expanded={open} data-testid={`bio-toggle-${m.member_id}`}>
        <div className="flex min-w-0 items-start gap-3">
          {selectable && <Checkbox checked={selected} onClick={stop} onCheckedChange={() => onSelect(m.member_id)} className="mt-1.5" aria-label={`Select ${m.name}`} data-testid={`bio-select-${m.member_id}`} />}
          <div className="min-w-0">
            <p className="font-heading text-lg font-bold text-slate-900" data-testid={`bio-name-${m.member_id}`}>
              {m.name}{m.role && <span className="ml-1.5 font-sans text-base font-medium text-slate-500" data-testid={`bio-role-${m.member_id}`}>({m.role})</span>}
            </p>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {!open && (
                <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${m.bio ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`} data-testid={`bio-status-${m.member_id}`}>
                  {m.bio ? "Bio ready" : "No bio yet"}
                </span>
              )}
              {m.groups.map((g) => <span key={g} className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">{g}</span>)}
            </div>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {!viewOnly && (
            <Button size="sm" variant="outline" className="rounded-full" onClick={(e) => { stop(e); onEdit(m); }} data-testid={`bio-edit-${m.member_id}`}>
              <Pencil className="mr-1.5 h-3.5 w-3.5" /> Edit
            </Button>
          )}
          <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
        </div>
      </div>
      {open && (
        <div className="mt-3" data-testid={`bio-body-${m.member_id}`}>
          {m.bio ? (
            <>
              <p className="whitespace-pre-line text-sm leading-relaxed text-slate-700" data-testid={`bio-text-${m.member_id}`}>{m.bio}</p>
              {m.updated_at && <p className="mt-2 text-xs text-slate-400">Updated {formatDate(m.updated_at)}{m.updated_by_name ? ` by ${m.updated_by_name}` : ""}</p>}
            </>
          ) : <Nudge m={m} viewOnly={viewOnly} onReminded={onReminded} />}
        </div>
      )}
    </Card>
  );
}
